import React, { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Pagination, Form, Row, Col, Spinner, Button, Modal } from 'react-bootstrap';
import { adminService } from '../../services/adminService';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '../../services/auth';
import toast from 'react-hot-toast';
import './AuditLogs.css';

const AuditLogs = () => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
    const [filters, setFilters] = useState({ action: '', targetId: '' });
    const [showClearModal, setShowClearModal] = useState(false);
    const [isClearing, setIsClearing] = useState(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({ action: '', targetId: '' });
    };

    const fetchLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const data = await adminService.getAuditLogs({
                page,
                limit: pagination.limit,
                ...filters
            });
            if (data.success) {
                setLogs(data.logs);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error('Fetch logs error:', error);
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.limit]);

    useEffect(() => {
        fetchLogs(1);
    }, [fetchLogs]);

    const handleClearLogs = async (range) => {
        if (!isAdmin()) return;

        setIsClearing(true);
        try {
            const data = await adminService.clearAuditLogs(range);
            if (data.success) {
                toast.success(t('clear_logs_success'));
                setShowClearModal(false);
                fetchLogs(1);
            }
        } catch (error) {
            toast.error(error.message || 'Error clearing logs');
        } finally {
            setIsClearing(false);
        }
    };

    const getActionBadge = (action) => {
        const variants = {
            'login': 'info',
            'agent_created': 'success',
            'agent_updated': 'warning',
            'agent_deleted': 'danger',
            'user_created': 'success',
            'user_updated': 'warning',
            'user_deleted': 'danger',
            'balance_adjusted': 'primary',
            'withdrawal_approved': 'success',
            'withdrawal_rejected': 'danger',
            'trade_placed': 'info',
            'trade_won': 'success',
            'trade_loss': 'danger'
        };
        return <Badge bg={variants[action] || 'secondary'}>{t(`audit_action_${action}`)}</Badge>;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-GB', { hour12: false });
    };

    const renderLogDetails = (log) => {
        const data = log.newData || {};
        if (Object.keys(data).length === 0) return '-';

        switch (log.action) {
            case 'login':
                return t('audit_detail_login') || 'User logged in';
            case 'balance_adjusted':
                const amount = data.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 });
                return `${t('type')}: ${data.type === 'add' ? t('add') : t('deduct')}, ${t('amount')}: ${amount}`;
            case 'trade_placed':
                return `${t('pair')}: ${data.pair}, ${t('type')}: ${data.type}, ${t('amount')}: ${data.amount}`;
            case 'trade_won':
                return `${t('profit')}: ${data.profit?.toFixed(2)}, ${t('amount')}: ${data.amount}`;
            case 'trade_loss':
                return `${t('loss')}: ${data.loss}, ${t('trade_id')}: ${data.tradeId?.substring(0, 8)}`;
            case 'user_created':
                return `${t('username')}: ${data.username}, ${t('email')}: ${data.email}`;
            case 'agent_created':
                return `${t('username')}: ${data.username}, ${t('role')}: ${data.role}`;
            case 'user_updated':
            case 'agent_updated':
                const keys = Object.keys(data).filter(k => k !== 'id' && k !== 'updatedAt');
                return keys.map(k => `${t(k) || k}: ${typeof data[k] === 'object' ? JSON.stringify(data[k]) : data[k]}`).join(' | ');
            case 'user_deleted':
            case 'agent_deleted':
                return `${t('username')}: ${data.username || '-'}`;
            case 'withdrawal_approved':
            case 'withdrawal_rejected':
                return `${t('withdrawal_id')}: ${data.withdrawalId?.substring(0, 8)}, ${t('amount')}: ${data.amount}`;
            case 'audit_logs_cleared':
                return `${t('range')}: ${data.range}, ${t('deleted_count')}: ${data.deletedCount}`;
            default:
                return JSON.stringify(data).substring(0, 150);
        }
    };

    return (
        <div className="majestic-page-wrapper">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0 border-start border-4 border-primary ps-2 text-white">{t('menu_audit_logs')}</h5>
                {isAdmin() && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setShowClearModal(true)}
                        className="filter-btn px-3"
                    >
                        <i className="bi bi-trash me-1"></i> {t('clear_history')}
                    </Button>
                )}
            </div>

            <div className="majestic-card filter-bar mb-4">
                <Row className="g-3 align-items-end">
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small text-secondary fw-bold mb-1">{t('audit_action')}</Form.Label>
                            <Form.Select
                                name="action"
                                value={filters.action}
                                onChange={handleFilterChange}
                                className="audit-filter-select"
                            >
                                <option value="">{t('all_actions')}</option>
                                <option value="login">{t('audit_action_login')}</option>
                                <option value="balance_adjusted">{t('audit_action_balance_adjusted')}</option>
                                <option value="trade_placed">{t('audit_action_trade_placed')}</option>
                                <option value="trade_won">{t('audit_action_trade_won')}</option>
                                <option value="trade_loss">{t('audit_action_trade_loss')}</option>
                                <option value="user_created">{t('audit_action_user_created')}</option>
                                <option value="user_updated">{t('audit_action_user_updated')}</option>
                                <option value="user_deleted">{t('audit_action_user_deleted')}</option>
                                <option value="agent_created">{t('audit_action_agent_created')}</option>
                                <option value="agent_updated">{t('audit_action_agent_updated')}</option>
                                <option value="withdrawal_approved">{t('audit_action_withdrawal_approved')}</option>
                                <option value="withdrawal_rejected">{t('audit_action_withdrawal_rejected')}</option>
                                <option value="audit_logs_cleared">{t('audit_logs_cleared_title') || t('clear_history')}</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label className="small text-secondary fw-bold mb-1">{t('target_id')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="targetId"
                                placeholder={t('enter_target_id')}
                                value={filters.targetId}
                                onChange={handleFilterChange}
                                className="audit-filter-input"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4} className="ms-auto">
                        <div className="d-flex gap-2">
                            <Button variant="primary" size="sm" className="filter-btn px-4" onClick={() => fetchLogs(1)}>
                                <i className="bi bi-search me-1"></i> {t('query')}
                            </Button>
                            <Button variant="secondary" size="sm" className="filter-btn px-3" onClick={() => { clearFilters(); fetchLogs(1); }}>
                                <i className="bi bi-arrow-counterclockwise me-1"></i> {t('reset')}
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>

            <div className="mobile-card-list">
                {logs.length > 0 ? logs.map(log => (
                    <div key={log.id} className="majestic-mobile-card">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div className="d-flex align-items-center">
                                <div className="rounded-circle bg-info bg-opacity-10 text-info d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                    <i className="bi bi-clock-history fs-5"></i>
                                </div>
                                <div>
                                    <div className="fw-bold text-white">{log.performedBy?.username || 'System'}</div>
                                    <div className="text-secondary extra-small">{formatDate(log.createdAt)}</div>
                                </div>
                            </div>
                            {getActionBadge(log.action)}
                        </div>

                        <div className="card-row">
                            <span className="card-label">{t('audit_operator')}</span>
                            <span className="card-value">
                                <span className="fw-bold">{log.performedBy?.username || 'System'}</span>
                                <span className="text-secondary extra-small ms-2">({log.performedBy?.role})</span>
                            </span>
                        </div>

                        <div className="card-row">
                            <span className="card-label">{t('audit_target')}</span>
                            <span className="card-value">{log.targetUser?.username || log.targetAgent?.username || '-'}</span>
                        </div>

                        <div className="card-row">
                            <span className="card-label">{t('audit_time')}</span>
                            <span className="card-value small text-secondary">{formatDate(log.createdAt)}</span>
                        </div>

                        <div className="card-row border-top border-light border-opacity-10 pt-2 mt-2">
                            <div className="w-100">
                                <span className="card-label d-block mb-1">{t('audit_details')}</span>
                                <span className="card-value text-secondary smaller text-break">
                                    {renderLogDetails(log)}
                                </span>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="text-center py-5 text-secondary">{t('no_data')}</div>
                )}
            </div>

            <div className="majestic-card p-0 majestic-table-container position-relative" style={{ minHeight: '200px' }}>
                {loading && (
                    <div className="position-absolute top-50 start-50 translate-middle" style={{ zIndex: 10 }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}
                <Table hover className={`border-0 mb-0 ${loading ? 'opacity-50' : ''}`}>
                    <thead>
                        <tr>
                            <th>{t('audit_time')}</th>
                            <th>{t('audit_operator')}</th>
                            <th>{t('audit_action')}</th>
                            <th>{t('audit_target')}</th>
                            <th>{t('audit_details')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.length > 0 ? logs.map(log => (
                            <tr key={log.id}>
                                <td className="small text-nowrap">{formatDate(log.createdAt)}</td>
                                <td>
                                    <div className="d-flex flex-column">
                                        <span className="fw-bold" style={{ color: 'var(--text-primary)' }}>{log.performedBy?.username || 'System'}</span>
                                        <span className="text-secondary smaller">{log.performedBy?.role}</span>
                                    </div>
                                </td>
                                <td>{getActionBadge(log.action)}</td>
                                <td style={{ color: 'var(--text-primary)' }}>
                                    {log.targetUser?.username || log.targetAgent?.username || '-'}
                                </td>
                                <td className="small text-secondary">
                                    {renderLogDetails(log)}
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="5" className="text-center p-4 text-secondary">{t('no_data')}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.First onClick={() => fetchLogs(1)} disabled={pagination.page === 1} />
                        <Pagination.Prev onClick={() => fetchLogs(pagination.page - 1)} disabled={pagination.page === 1} />
                        {[...Array(pagination.pages)].map((_, idx) => (
                            <Pagination.Item
                                key={idx + 1}
                                active={idx + 1 === pagination.page}
                                onClick={() => fetchLogs(idx + 1)}
                            >
                                {idx + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => fetchLogs(pagination.page + 1)} disabled={pagination.page === pagination.pages} />
                        <Pagination.Last onClick={() => fetchLogs(pagination.pages)} disabled={pagination.page === pagination.pages} />
                    </Pagination>
                </div>
            )}

            {/* Clear History Confirmation Modal */}
            <Modal
                show={showClearModal}
                onHide={() => setShowClearModal(false)}
                centered
                className="majestic-modal"
            >
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="text-white fs-5">{t('clear_logs_title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <p className="text-secondary small mb-4">{t('clear_logs_confirm')}</p>
                    <div className="d-grid gap-2">
                        <Button variant="outline-warning" onClick={() => handleClearLogs('1h')} disabled={isClearing}>
                            {t('past_hour')}
                        </Button>
                        <Button variant="outline-warning" onClick={() => handleClearLogs('1d')} disabled={isClearing}>
                            {t('past_day')}
                        </Button>
                        <Button variant="outline-warning" onClick={() => handleClearLogs('1w')} disabled={isClearing}>
                            {t('past_week')}
                        </Button>
                        <Button variant="outline-warning" onClick={() => handleClearLogs('1m')} disabled={isClearing}>
                            {t('past_month')}
                        </Button>
                        <Button variant="outline-danger" className="mt-2" onClick={() => handleClearLogs('all')} disabled={isClearing}>
                            {t('all_time')}
                        </Button>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="link" onClick={() => setShowClearModal(false)} className="text-secondary text-decoration-none">
                        {t('cancel')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AuditLogs;
